import { useStore } from '@nanostores/react';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ANALYTICS_EVENTS } from '@dunea/shared';
import { setAffiliateRefIfAbsent } from '~/lib/affiliateRef';
import { apiTrackAffiliateClick } from '~/lib/api/affiliates';
import { apiGetChats } from '~/lib/api/chats';
import { apiGetListing } from '~/lib/api/marketplace';
import { capture } from '~/lib/posthog';
import { $authLoading, $isAuthenticated } from '~/lib/stores/auth';
import { $marketplaceIntent } from '~/lib/stores/marketplaceIntent';
import { workbenchStore } from '~/lib/stores/workbench';
import {
  mostRecentChat,
  panelTarget,
  parseStorefrontParams,
  readIntent,
  serializeIntent,
  withoutParams,
  type StorefrontIntent,
} from '~/lib/storefront-links';
import { marketplaceReturn } from '~/components/workbench/tools/marketplace/marketplace-model';
import {
  pendingRemixSlug,
  remixTemplate,
  resumePaidRemix,
} from '~/components/workbench/tools/marketplace/install-actions';

const INTENT_KEY = 'dunea_storefront_intent';

function stash(intent: StorefrontIntent) {
  try {
    localStorage.setItem(INTENT_KEY, serializeIntent(intent));
  } catch {
    // storage blocked: the intent only survives this page view
  }
}

function takeStashed(): StorefrontIntent | null {
  try {
    const intent = readIntent(localStorage.getItem(INTENT_KEY));
    localStorage.removeItem(INTENT_KEY);

    return intent;
  } catch {
    return null;
  }
}

/** Same attribution as /r/:code (AffiliateCapturePage): first-touch cookie, click ping, PostHog event. */
function captureRef(code: string, landing: string) {
  setAffiliateRefIfAbsent(code);
  apiTrackAffiliateClick(code, landing).catch(() => undefined);
  capture(ANALYTICS_EVENTS.AFFILIATE_LINK_CLICKED, { code, landing });
}

/**
 * Storefront deep links from dunea.ai, on any route: `?marketplace=<slug>`,
 * `?remix=<slug>`, `?expert=<handle>`, `?wishlist=1`, `?wish=<id>`, `&ref=<code>`.
 * The params are read on the first render (before an auth redirect drops the
 * query), stashed across sign-in, stripped from the URL, and acted on once
 * the user is signed in. Also resumes a paid template remix when Checkout
 * returns to a page without the workbench.
 */
export function useStorefrontLinks(): void {
  const isAuthenticated = useStore($isAuthenticated);
  const authLoading = useStore($authLoading);
  const location = useLocation();
  const navigate = useNavigate();
  const captured = useRef<ReturnType<typeof parseStorefrontParams> | null>(null);
  const busy = useRef(false);

  if (captured.current === null && typeof window !== 'undefined') {
    captured.current = parseStorefrontParams(window.location.search);

    if (captured.current.intent) {
      stash(captured.current.intent);
    }
  }

  // Attribution + URL cleanup: once per load, signed in or not.
  useEffect(() => {
    const parsed = captured.current;

    if (!parsed || parsed.handled.length === 0) {
      return;
    }

    if (parsed.ref) {
      captureRef(parsed.ref, `${window.location.pathname}${window.location.search}`);
    }

    window.history.replaceState(window.history.state, '', withoutParams(window.location.href, parsed.handled));
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthenticated || busy.current) {
      return;
    }

    const onChat = location.pathname.startsWith('/chat/');

    // Paid remix Checkout that came back somewhere without the Marketplace panel.
    const ret = marketplaceReturn(window.location.search);

    if (!onChat && ret?.outcome === 'success' && pendingRemixSlug()) {
      busy.current = true;
      window.history.replaceState(
        window.history.state,
        '',
        withoutParams(window.location.href, ['marketplace', 'install', 'panel']),
      );
      void resumePaidRemix(navigate).finally(() => {
        busy.current = false;
      });

      return;
    }

    const intent = takeStashed();

    if (!intent) {
      return;
    }

    busy.current = true;
    void run(intent, onChat).finally(() => {
      busy.current = false;
    });
  }, [authLoading, isAuthenticated, location.pathname]);

  async function run(intent: StorefrontIntent, onChat: boolean) {
    if (intent.kind === 'remix') {
      try {
        const result = await remixTemplate(intent.slug);

        if (result !== 'redirecting') {
          toast.success('Your new site is ready.');
          navigate(result.path);
        }
      } catch (err) {
        console.error(err);
        toast.error('Could not start from that template.');
      }

      return;
    }

    const target = panelTarget(intent);
    const open = () => {
      $marketplaceIntent.set({ ...target, source: 'storefront' });
      workbenchStore.openTool('marketplace');
    };

    if (onChat) {
      open();
      return;
    }

    const chats = await apiGetChats().catch(() => []);
    const recent = mostRecentChat(chats);

    if (recent) {
      $marketplaceIntent.set({ ...target, source: 'storefront' });
      navigate(`/chat/${recent.urlId || recent.id}`);

      return;
    }

    let name = 'it';

    if (target.slug) {
      name = await apiGetListing(target.slug)
        .then((l) => l.name)
        .catch(() => target.slug ?? 'it');
    }

    navigate('/projects');
    toast.info(target.slug ? `Open an app to install ${name}` : 'Open an app to use the Marketplace');
  }
}
